import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Download, CalendarClock, AlertTriangle, CheckCircle2, Clock, Flame } from "lucide-react";
import { toast } from "sonner";
import { useRepFilter } from "@/context/RepFilterContext";

const COLUMNS = [
  { status: "open",        label: "Open",        icon: Clock,          grad: "from-amber-500 to-orange-500",  soft: "bg-amber-50/60 border-amber-200" },
  { status: "in_progress", label: "In progress", icon: Flame,          grad: "from-sky-600 to-indigo-600",    soft: "bg-sky-50/60 border-sky-200" },
  { status: "done",        label: "Done",        icon: CheckCircle2,   grad: "from-emerald-600 to-emerald-500", soft: "bg-emerald-50/60 border-emerald-200" },
  { status: "cancelled",   label: "Cancelled",   icon: AlertTriangle,  grad: "from-slate-500 to-slate-600",     soft: "bg-slate-50 border-slate-200" },
];

const PRIO = {
  high:   { pill: "bg-rose-100 text-rose-800 border-rose-200",     stripe: "bg-rose-500" },
  medium: { pill: "bg-amber-100 text-amber-800 border-amber-200",  stripe: "bg-amber-500" },
  low:    { pill: "bg-slate-100 text-slate-700 border-slate-200",  stripe: "bg-slate-400" },
};

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function FollowUps() {
  const [rows, setRows] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const { repId: globalRep, setRepId: setGlobalRep, isAdmin } = useRepFilter();
  const [repFilter, setRepFilterLocal] = useState(globalRep);
  useEffect(() => { setRepFilterLocal(globalRep); }, [globalRep]);
  const setRepFilter = (v) => { setRepFilterLocal(v); if (isAdmin && v !== "unassigned") setGlobalRep(v); };
  const [form, setForm] = useState({ facility_id: "", title: "", due_date: todayIso(), priority: "medium", status: "open", assigned_to: "none", notes: "" });

  const load = () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/followups", { params }).then(({ data }) => setRows(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => {
    api.get("/facilities").then(({ data }) => setFacilities(data));
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  }, []);

  const submit = async () => {
    try {
      const payload = { ...form, assigned_to: form.assigned_to === "none" ? null : form.assigned_to };
      await api.post("/followups", payload);
      toast.success("Follow-up added");
      setOpen(false); setForm({ ...form, title: "", notes: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const setStatus = async (id, status) => { await api.patch(`/followups/${id}`, { status }); load(); };

  const filteredRows = useMemo(() => {
    if (repFilter === "all") return rows;
    if (repFilter === "unassigned") return rows.filter((r) => !r.assigned_to);
    return rows.filter((r) => r.assigned_to === repFilter);
  }, [rows, repFilter]);

  const grouped = useMemo(() => {
    const g = { open: [], in_progress: [], done: [], cancelled: [] };
    filteredRows.forEach((r) => { (g[r.status] || g.open).push(r); });
    return g;
  }, [filteredRows]);

  const stats = useMemo(() => {
    const today = todayIso();
    return {
      open: grouped.open.length + grouped.in_progress.length,
      overdue: filteredRows.filter((r) => (r.status === "open" || r.status === "in_progress") && r.due_date < today).length,
      due_today: filteredRows.filter((r) => (r.status === "open" || r.status === "in_progress") && r.due_date === today).length,
      done: grouped.done.length,
    };
  }, [filteredRows, grouped]);

  const exportCsv = async () => {
    const token = localStorage.getItem("aceh_token") || "";
    const resp = await fetch(`${API}/followups/export.csv`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await resp.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "followups.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="followups-page">
      {/* Colorful hero */}
      <div className="rounded-xl overflow-hidden mb-6 text-white p-6 md:p-8" style={{ background: "linear-gradient(135deg, var(--hero-from) 0%, var(--hero-via) 55%, var(--hero-to) 100%)" }}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono-plex text-white/80 flex items-center gap-1.5"><CalendarClock className="w-3 h-3" /> Tasks that need attention</div>
            <h1 className="font-heading text-4xl md:text-5xl font-black mt-1">Follow-ups</h1>
            <p className="text-white/85 mt-2 text-sm max-w-lg">Every promise you made to a facility, in one bright place.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger className="w-44 bg-white/15 backdrop-blur-md border-white/30 text-white" data-testid="fu-rep-filter"><SelectValue placeholder="All reps" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reps</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-white/15 backdrop-blur-md border-white/30 text-white" data-testid="fu-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem><SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem><SelectItem value="done">Done</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" onClick={exportCsv} className="text-white hover:bg-white/15 border border-white/30"><Download className="w-4 h-4 mr-1" /> CSV</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="fu-new-btn" className="bg-white text-slate-900 hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> New</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New follow-up</DialogTitle></DialogHeader>
                <div className="grid gap-3">
                  <div><Label>Title</Label><Input data-testid="fu-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
                  <div><Label>Facility</Label>
                    <Select value={form.facility_id} onValueChange={v => setForm({ ...form, facility_id: v })}>
                      <SelectTrigger data-testid="fu-facility"><SelectValue placeholder="Select facility" /></SelectTrigger>
                      <SelectContent>{facilities.map(f => <SelectItem key={f.facility_id} value={f.facility_id}>{f.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
                    <div><Label>Priority</Label>
                      <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Assign to</Label>
                    <Select value={form.assigned_to} onValueChange={v => setForm({ ...form, assigned_to: v })}>
                      <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Unassigned</SelectItem>{users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} data-testid="fu-save" className="bg-slate-900 hover:bg-slate-800">Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stat pills inside hero */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg bg-white/15 backdrop-blur-md p-3">
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/80">Open</div>
            <div className="font-heading font-black text-3xl">{stats.open}</div>
          </div>
          <div className="rounded-lg bg-white/15 backdrop-blur-md p-3">
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/80">Due today</div>
            <div className="font-heading font-black text-3xl">{stats.due_today}</div>
          </div>
          <div className="rounded-lg bg-rose-600/60 backdrop-blur-md p-3 ring-1 ring-white/30">
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/90">Overdue</div>
            <div className="font-heading font-black text-3xl">{stats.overdue}</div>
          </div>
          <div className="rounded-lg bg-white/15 backdrop-blur-md p-3">
            <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/80">Done</div>
            <div className="font-heading font-black text-3xl">{stats.done}</div>
          </div>
        </div>
      </div>

      {/* Kanban lanes */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="followups-board">
        {COLUMNS.map((col) => {
          const items = grouped[col.status] || [];
          const Icon = col.icon;
          return (
            <div key={col.status} className={`rounded-lg border ${col.soft} p-3 min-h-[420px]`} data-testid={`fu-col-${col.status}`}>
              <div className={`rounded-md p-3 mb-3 bg-gradient-to-r ${col.grad} text-white flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <div className="font-heading font-bold">{col.label}</div>
                </div>
                <Badge className="bg-white/20 hover:bg-white/20 text-white border-0 font-mono-plex">{items.length}</Badge>
              </div>
              <div className="space-y-2">
                {items.length === 0 && <div className="text-xs text-slate-400 italic py-4 text-center">Nothing here.</div>}
                {items.map((r) => {
                  const overdue = (r.status === "open" || r.status === "in_progress") && r.due_date < todayIso();
                  const stripe = PRIO[r.priority] || PRIO.medium;
                  return (
                    <Card key={r.followup_id} className={`p-3 border-slate-200 shadow-none bg-white row-enter relative overflow-hidden`} data-testid={`fu-row-${r.followup_id}`}>
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${stripe.stripe}`} />
                      <div className="pl-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-900 leading-snug">{r.title}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 truncate">{r.facility_name}</div>
                          </div>
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${stripe.pill}`}>{r.priority}</Badge>
                        </div>
                        {r.notes && <div className="text-[11px] text-slate-600 mt-2 line-clamp-2">{r.notes}</div>}
                        <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                          <div className={`text-[11px] font-mono-plex ${overdue ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                            {overdue && "⚠ "}{r.due_date}
                          </div>
                          <Select value={r.status} onValueChange={v => setStatus(r.followup_id, v)}>
                            <SelectTrigger className="w-28 h-7 text-[11px]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In progress</SelectItem>
                              <SelectItem value="done">Done</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {r.assigned_to_name && <div className="text-[10px] font-mono-plex text-slate-400 mt-1">→ {r.assigned_to_name}</div>}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
